import React from 'react';
import { Button as AntButton } from 'antd';
import type { ButtonProps as AntButtonProps } from 'antd';
import { cn } from '@/lib/utils';
import { colors } from '../../theme/colors';

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'link' | 'social' | 'danger' | 'danger-outline' | 'warning' | 'teal' | 'teal-outline';
export type ButtonSize = 'small' | 'medium' | 'large';
export interface ButtonProps extends Omit<AntButtonProps, 'type' | 'size' | 'variant'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
  children?: React.ReactNode;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
}

// Size configuration
const sizeConfig = {
  small: { height: '2rem', padding: '0 0.75rem', fontSize: '0.875rem' },
  medium: { height: '2.5rem', padding: '0 1rem', fontSize: '14px' },
  large: { height: '3rem', padding: '0 1.5rem', fontSize: '1rem' },
} as const;

// Variant styles configuration
type VariantStyleConfig = {
  base: React.CSSProperties;
  hover: React.CSSProperties;
  disabled: React.CSSProperties;
};

const variantConfigs: Record<ButtonVariant, VariantStyleConfig> = {
  primary: {
    base: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
      color: colors.white,
    },
    hover: {
      backgroundColor: colors.primaryHover,
      borderColor: colors.primaryHover,
    },
    disabled: {
      backgroundColor: colors.surfaceSubtle,
      borderColor: colors.surface,
      color: colors.textMuted,
    },
  },
  secondary: {
    base: {
      backgroundColor: colors.surfaceLighter,
      borderColor: colors.surfaceSubtle,
      color: colors.textDefault,
    },
    hover: {
      backgroundColor: colors.surfaceLight,
      borderColor: colors.surface,
    },
    disabled: {
      backgroundColor: colors.surfaceLighter,
      borderColor: colors.surfaceLight,
      color: colors.textWeak,
    },
  },
  outline: {
    base: {
      backgroundColor: colors.transparent,
      borderColor: colors.primary,
      borderWidth: 2,
      color: colors.primary,
    },
    hover: {
      backgroundColor: colors.primaryExtraLight,
    },
    disabled: {
      backgroundColor: colors.transparent,
      borderColor: colors.surfaceSubtle,
      borderWidth: 2,
      color: colors.textWeak,
    },
  },
  ghost: {
    base: {
      backgroundColor: colors.transparent,
      borderColor: colors.transparent,
      color: colors.textMuted,
    },
    hover: {
      backgroundColor: colors.transparent,
      color: colors.textDefault,
    },
    disabled: {
      backgroundColor: colors.transparent,
      borderColor: colors.transparent,
      color: colors.textWeak,
    },
  },
  link: {
    base: {
      backgroundColor: colors.transparent,
      borderColor: colors.transparent,
      color: colors.primary,
      padding: 0,
      height: 'auto',
    },
    hover: {
      color: colors.primaryHover,
    },
    disabled: {
      backgroundColor: colors.transparent,
      borderColor: colors.transparent,
      color: colors.textWeak,
      padding: 0,
      height: 'auto',
    },
  },
  social: {
    base: {
      backgroundColor: colors.info,
      borderColor: colors.info,
      color: colors.white,
    },
    hover: {
      backgroundColor: colors.infoStrong,
      borderColor: colors.infoStrong,
    },
    disabled: {
      backgroundColor: colors.surfaceSubtle,
      borderColor: colors.surface,
      color: colors.textMuted,
    },
  },
  danger: {
    base: {
      backgroundColor: colors.error,
      borderColor: colors.error,
      color: colors.white,
    },
    hover: {
      backgroundColor: colors.errorStrong,
      borderColor: colors.errorStrong,
    },
    disabled: {
      backgroundColor: colors.surfaceSubtle,
      borderColor: colors.surface,
      color: colors.textMuted,
    },
  },
  'danger-outline': {
    base: {
      backgroundColor: colors.transparent,
      borderColor: colors.error,
      color: colors.error,
    },
    hover: {
      backgroundColor: colors.errorBgSubtle,
      borderColor: colors.errorStrong,
      color: colors.errorStrong,
    },
    disabled: {
      backgroundColor: colors.transparent,
      borderColor: colors.surfaceSubtle,
      color: colors.textWeak,
    },
  },
  warning: {
    base: {
      backgroundColor: colors.warning,
      borderColor: colors.warning,
      color: colors.white,
    },
    hover: {
      backgroundColor: colors.warningStrong,
      borderColor: colors.warningStrong,
    },
    disabled: {
      backgroundColor: colors.surfaceSubtle,
      borderColor: colors.surface,
      color: colors.textMuted,
    },
  },
  teal: {
    base: {
      backgroundColor: colors.teal,
      borderColor: colors.teal,
      color: colors.white,
    },
    hover: {
      backgroundColor: colors.tealStrong,
      borderColor: colors.tealStrong,
    },
    disabled: {
      backgroundColor: colors.surfaceSubtle,
      borderColor: colors.surface,
      color: colors.textMuted,
    },
  },
  'teal-outline': {
    base: {
      backgroundColor: colors.transparent,
      borderColor: colors.teal,
      color: colors.teal,
    },
    hover: {
      backgroundColor: colors.tealBgSubtle,
      borderColor: colors.tealStrong,
      color: colors.tealStrong,
    },
    disabled: {
      backgroundColor: colors.transparent,
      borderColor: colors.surfaceSubtle,
      color: colors.textWeak,
    },
  },
};

// Get variant styles based on state
const getVariantStyles = (
  variant: ButtonVariant,
  disabled: boolean,
  isHovered: boolean
): React.CSSProperties => {
  const config = variantConfigs[variant];

  if (disabled) {
    return config.disabled;
  }

  return isHovered ? { ...config.base, ...config.hover } : config.base;
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  children,
  leftIcon,
  rightIcon,
  loading = false,
  disabled = false,
  onClick,
  className = '',
  style,
  ...props
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const { height, padding, fontSize } = sizeConfig[size];
  const variantStyles = getVariantStyles(variant, disabled, isHovered);
  const buttonStyle: React.CSSProperties = {
    height: variant === 'link' ? 'auto' : height,
    padding: variant === 'link' ? 0 : padding,
    fontSize,
    fontWeight: 400,
    borderRadius: 3,
    ...variantStyles,
    width: fullWidth ? '100%' : undefined,
    ...style,
  };
  return (
    <AntButton
      className={cn(
        'inline-flex items-center justify-center gap-2 transition-all duration-200',
        disabled && 'cursor-not-allowed',
        className
      )}
      style={buttonStyle}
      loading={loading}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...props}
    >
      {leftIcon && <span className="flex items-center">{leftIcon}</span>}
      {children}
      {rightIcon && <span className="flex items-center">{rightIcon}</span>}
    </AntButton>
  );
};

export default Button;
